import React, { useState, useEffect, useCallback } from 'react';
import { TabsContent } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { subdivideApi, employeesApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Banknote, Layers, Scale, Loader2, Pencil, Wand2, AlertTriangle, HandCoins } from 'lucide-react';

const fmtAcres = (a) => (Number(a) || 0).toFixed(2);
const fmtPct = (p) => `${((Number(p) || 0) * 100).toFixed(1)}%`;

function RebalanceValue({ value }) {
  const v = Number(value) || 0;
  const cls = v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-600' : 'text-muted-foreground';
  const label = v > 0 ? ' (owed back)' : v < 0 ? ' (underfunded)' : '';
  return <span className={`font-semibold ${cls}`}>{formatCurrency(v)}<span className="text-xs font-normal">{label}</span></span>;
}

export function SubdivideMasterTab({ projectId, project, isOwner, isSuperOwner }) {
  const [capTable, setCapTable] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [closings, setClosings] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLotForm, setShowLotForm] = useState(false);
  const [lotForm, setLotForm] = useState({ name: '', acres: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cap, lots, cls] = await Promise.all([
        subdivideApi.getCapTable(projectId),
        subdivideApi.getLots(projectId),
        subdivideApi.getClosings(projectId),
      ]);
      setCapTable(cap.data);
      setMatrix(lots.data);
      setClosings(cls.data || []);
      if (isOwner) {
        try {
          const comm = await subdivideApi.getClosingCommissions(projectId);
          setCommissions(comm.data);
        } catch (e) {
          console.debug('Closing commissions unavailable', e?.message);
        }
      }
    } catch (e) {
      console.error('Failed to load subdivide data', e);
      toast.error('Failed to load subdivide data');
    } finally {
      setLoading(false);
    }
  }, [projectId, isOwner]);

  useEffect(() => { load(); }, [load]);

  const addLot = async () => {
    if (!lotForm.name || !lotForm.acres) { toast.error('Enter a lot name and acreage'); return; }
    try {
      await subdivideApi.createLot(projectId, { name: lotForm.name, acres: parseFloat(lotForm.acres) });
      toast.success('Lot added');
      setLotForm({ name: '', acres: '' });
      setShowLotForm(false);
      load();
    } catch (e) { toast.error('Failed to add lot'); }
  };

  const removeLot = async (lotId) => {
    try { await subdivideApi.deleteLot(lotId); toast.success('Lot deleted'); load(); }
    catch (e) { toast.error('Failed to delete lot'); }
  };

  const heldLots = (matrix?.lots || []).filter((l) => l.status !== 'sold');

  if (loading) {
    return (
      <TabsContent value="subdivide">
        <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="subdivide-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading subdivide model…
        </div>
      </TabsContent>
    );
  }

  return (
    <TabsContent value="subdivide" className="space-y-4" data-testid="subdivide-master-tab">
      {/* Legacy → Master migration CTA (parent projects only, until converted) */}
      {isOwner && !project?.is_subdivide_master && !project?.parent_project_id && (
        <ConvertToMasterCard projectId={projectId} onConverted={load} />
      )}

      {/* ── CAP TABLE ── */}
      <Card className="rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Scale className="w-4 h-4" /> Cap Table &amp; Rebalancing
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {capTable && (
            <table className="w-full text-sm" data-testid="cap-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-4 font-medium">Party</th>
                  <th className="py-2 px-4 font-medium text-right">Actual Cash Funded</th>
                  <th className="py-2 px-4 font-medium text-right">Contractual Hurdle</th>
                  <th className="py-2 pl-4 font-medium text-right">Rebalancing Position</th>
                </tr>
              </thead>
              <tbody>
                {['investor', 'manager'].map((k) => (
                  <tr key={k} className="border-b last:border-0" data-testid={`cap-row-${k}`}>
                    <td className="py-2 pr-4 font-medium">{capTable[k].entity}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(capTable[k].funded)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(capTable[k].hurdle)}</td>
                    <td className="py-2 pl-4 text-right"><RebalanceValue value={capTable[k].rebalance} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-xs text-muted-foreground mt-3">
            A positive rebalance means that party fronted more cash than their contractual responsibility and is repaid first (senior) at the next closing.
          </p>
        </CardContent>
      </Card>

      {/* ── LOT MATRIX ── */}
      <Card className="rounded-sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="w-4 h-4" /> Lot Matrix
          </CardTitle>
          {isOwner && (
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setShowLotForm((s) => !s)} data-testid="add-lot-toggle">
              <Plus className="w-4 h-4 mr-1" /> Add Lot
            </Button>
          )}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {showLotForm && (
            <div className="flex flex-wrap items-end gap-3 mb-4 p-3 bg-muted/40 rounded-sm" data-testid="lot-form">
              <div className="space-y-1">
                <Label className="text-xs">Lot Name</Label>
                <Input className="h-9 w-40" value={lotForm.name} onChange={(e) => setLotForm({ ...lotForm, name: e.target.value })} placeholder="Lot 1" data-testid="lot-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Acres</Label>
                <Input className="h-9 w-28" type="number" step="0.0001" value={lotForm.acres} onChange={(e) => setLotForm({ ...lotForm, acres: e.target.value })} placeholder="4.33" data-testid="lot-acres-input" />
              </div>
              <Button size="sm" className="rounded-sm" onClick={addLot} data-testid="save-lot-btn">Save</Button>
            </div>
          )}
          <table className="w-full text-sm" data-testid="lot-matrix-table">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-4 font-medium">Lot</th>
                <th className="py-2 px-4 font-medium text-right">Acres</th>
                <th className="py-2 px-4 font-medium text-right">% of Master</th>
                <th className="py-2 px-4 font-medium text-right">Cost Basis</th>
                <th className="py-2 px-4 font-medium text-center">Status</th>
                {isOwner && <th className="py-2 pl-4"></th>}
              </tr>
            </thead>
            <tbody>
              {(matrix?.lots || []).map((l) => (
                <tr key={l.lot_id} className="border-b last:border-0" data-testid={`lot-row-${l.lot_id}`}>
                  <td className="py-2 pr-4 font-medium">{l.name}</td>
                  <td className="py-2 px-4 text-right">{fmtAcres(l.acres)}</td>
                  <td className="py-2 px-4 text-right">{fmtPct(l.acreage_pct)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(l.cost_basis)}</td>
                  <td className="py-2 px-4 text-center">
                    <Badge variant={l.status === 'sold' ? 'default' : 'secondary'} className="rounded-sm capitalize">{l.status}</Badge>
                  </td>
                  {isOwner && (
                    <td className="py-2 pl-4 text-right">
                      {l.status !== 'sold' && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeLot(l.lot_id)} data-testid={`delete-lot-${l.lot_id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {(matrix?.lots || []).length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No lots yet. Add lots to allocate cost basis.</td></tr>
              )}
            </tbody>
            {matrix && (matrix.lots || []).length > 0 && (
              <tfoot>
                <tr className="border-t font-medium">
                  <td className="py-2 pr-4">Total ({matrix.master_acreage} ac)</td>
                  <td colSpan={2}></td>
                  <td className="py-2 px-4 text-right">{formatCurrency(matrix.total_cost_basis)}</td>
                  <td colSpan={isOwner ? 2 : 1} className="py-2 px-4 text-right text-xs text-muted-foreground">
                    Held basis: {formatCurrency(matrix.retained_held_basis)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

      {/* ── CLOSINGS ── */}
      <Card className="rounded-sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Banknote className="w-4 h-4" /> Closings &amp; Payouts
          </CardTitle>
          {isOwner && (
            <RecordClosingDialog projectId={projectId} heldLots={heldLots} onSaved={load} />
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {closings.length === 0 && (
            <p className="py-6 text-center text-muted-foreground">No closings recorded. Record a closing when one or more lots sell.</p>
          )}
          {closings.map((c) => (
            <ClosingCard key={c.id} closing={c} matrix={matrix} projectId={projectId}
              allLots={matrix?.lots || []} isOwner={isOwner} isSuperOwner={isSuperOwner}
              commissions={commissions} onChanged={load} />
          ))}
        </CardContent>
      </Card>
    </TabsContent>
  );
}

function ConvertToMasterCard({ projectId, onConverted }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const preview = async () => {
    setLoading(true);
    try {
      const r = await subdivideApi.convertToMaster(projectId, true);
      setReport(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
    finally { setLoading(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      await subdivideApi.convertToMaster(projectId, false);
      toast.success('Converted to Subdivide Master');
      onConverted();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Conversion failed'); }
    finally { setCommitting(false); }
  };

  return (
    <Card className="rounded-sm border-amber-300 bg-amber-50/50" data-testid="convert-to-master-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wand2 className="w-4 h-4" /> Convert to Subdivide Master
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          This deal uses the legacy per-child-lot model, where profit/ROI can show incorrect
          negatives. Convert it to the clean Subdivide Master model — child lots become a lot
          matrix, sales become closing events, and payouts use the staggered-sale waterfall.
          Nothing is deleted; legacy lots are archived and redirected here.
        </p>
        {!report && (
          <Button size="sm" variant="outline" className="rounded-sm" onClick={preview} disabled={loading} data-testid="preview-convert-btn">
            {loading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Preview Conversion
          </Button>
        )}
        {report && (
          <div className="space-y-3">
            <div className="bg-white rounded-sm p-3 border" data-testid="convert-report">
              <p className="font-medium mb-1">Reconciliation preview</p>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                <span>Lots: <strong>{report.lots_created}</strong></span>
                <span>Closings: <strong>{report.closings_created}</strong></span>
                <span>Total cost basis: <strong>{formatCurrency(report.total_cost_basis)}</strong></span>
                <span>Retained held basis: <strong>{formatCurrency(report.retained_held_basis)}</strong></span>
                {report.synthesized_purchase != null && (
                  <span className="sm:col-span-2">Synthesized purchase: <strong>{formatCurrency(report.synthesized_purchase)}</strong></span>
                )}
              </div>
              {report.closings?.map((c, i) => (
                <div key={i} className="mt-2 pt-2 border-t text-xs">
                  <p className="text-muted-foreground">{c.closing_date} · resale {formatCurrency(c.gross_resale_price)} · HUD {formatCurrency(c.actual_closing_costs)}</p>
                  <p>Investor wire <strong>{formatCurrency(c.investor_wire)}</strong> · Manager wire <strong>{formatCurrency(c.manager_wire)}</strong> · balance {formatCurrency(c.balance_check)}</p>
                </div>
              ))}
            </div>
            {report.warnings?.length > 0 && (
              <div className="text-xs text-amber-700 space-y-1">
                {report.warnings.map((w, i) => (
                  <p key={i} className="flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {w}</p>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setReport(null)}>Cancel</Button>
              <Button size="sm" className="rounded-sm" onClick={commit} disabled={committing} data-testid="commit-convert-btn">
                {committing && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Convert Now
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PayoutRow({ label, value, strong }) {
  return (
    <div className={`flex justify-between py-1 ${strong ? 'font-semibold' : ''}`}>
      <span className="text-muted-foreground">{label}</span>
      <span>{formatCurrency(value)}</span>
    </div>
  );
}

function ClosingCard({ closing, matrix, projectId, allLots, isOwner, isSuperOwner, commissions, onChanged }) {
  const p = closing.payout || {};
  const lotNames = (closing.lot_ids || []).map((id) => (matrix?.lots || []).find((l) => l.lot_id === id)?.name || id).join(', ');
  const del = async () => {
    try { await subdivideApi.deleteClosing(closing.id); toast.success('Closing deleted'); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete closing'); }
  };
  return (
    <div className="border rounded-sm p-4" data-testid={`closing-card-${closing.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="font-medium">{closing.closing_date} — {lotNames || 'No lots'}</p>
          <p className="text-xs text-muted-foreground">
            Resale {formatCurrency(p.gross_resale_price)} · HUD costs {formatCurrency(p.actual_closing_costs)} · Hold {p.days_held}d · Investor split {p.investor_split_pct}%
          </p>
        </div>
        <div className="flex items-center gap-1">
          {isOwner && (
            <RecordClosingDialog projectId={projectId} heldLots={allLots} allLots={allLots}
              existingClosing={closing} onSaved={onChanged}
              trigger={<Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`edit-closing-${closing.id}`}><Pencil className="w-4 h-4" /></Button>} />
          )}
          {isSuperOwner && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`delete-closing-${closing.id}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this closing?</AlertDialogTitle>
                  <AlertDialogDescription>The lots it sold will revert to "held". This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={del}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
        <div>
          <PayoutRow label="Net Cash Available" value={p.net_cash_available} strong />
          <PayoutRow label="Tier 1 — Capital Rebalance" value={p.tier1_rebalance_total} />
          <PayoutRow label="Tier 2 — Sold-Lot Capital" value={p.tier2_sold_lot_capital} />
          <PayoutRow label="Tier 3 — Net Profit" value={p.cash_after_tier2_net_profit} strong />
          <PayoutRow label="Retained Held-Lot Basis" value={p.retained_held_basis} />
        </div>
        <div className="bg-muted/40 rounded-sm p-3">
          <p className="text-xs font-medium text-muted-foreground mb-1">Final Bank Wires</p>
          {(p.partner_wires || []).map((w) => (
            <div key={w.partner_id} className="flex justify-between py-1" data-testid={`wire-investor-${w.partner_id}`}>
              <span>{w.partner_name}</span>
              <span className="font-semibold">{formatCurrency(w.total_wire)}</span>
            </div>
          ))}
          <div className="flex justify-between py-1 border-t mt-1" data-testid="wire-manager">
            <span>{p.manager_wire?.entity || 'Manager'}</span>
            <span className="font-semibold">{formatCurrency(p.manager_wire?.total_wire)}</span>
          </div>
          {(p.coinvestor_wires || []).length > 0 && (
            <div className="mt-2 pt-2 border-t" data-testid={`coinvestor-wires-${closing.id}`}>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Per-Investor Split (funded share)</p>
              {p.coinvestor_wires.map((w) => (
                <div key={w.id} className="flex justify-between py-0.5 text-xs" data-testid={`coinvestor-wire-${closing.id}-${w.id}`}>
                  <span className="text-muted-foreground">{w.name} <span className="text-[10px]">({w.funded_pct}%)</span></span>
                  <span className="font-mono font-semibold text-emerald-700">{formatCurrency(w.total_wire)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {isOwner && (
        <ClosingCommissions closing={closing} projectId={projectId} commissions={commissions} onChanged={onChanged} />
      )}
    </div>
  );
}

function ClosingCommissions({ closing, projectId, commissions, onChanged }) {
  const p = closing.payout || {};
  const managerProfit = Number(p.manager_profit) || 0;
  const employees = commissions?.employees || [];
  const eligible = employees.filter((e) => e.is_eligible);
  const pool = eligible.length > 0 ? eligible : employees;
  const recorded = (commissions?.entries || []).filter((e) => e.related_closing_id === closing.id);
  const recordedIds = new Set(recorded.map((r) => r.employee_id));
  const totalCommission = recorded.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div className="mt-3 pt-3 border-t" data-testid={`closing-commissions-${closing.id}`}>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
          <HandCoins className="w-3.5 h-3.5" /> Employee Commissions
          <span className="text-muted-foreground/70">· LF profit base {formatCurrency(managerProfit)}</span>
        </p>
        <RecordClosingCommissionDialog
          closing={closing}
          projectId={projectId}
          managerProfit={managerProfit}
          employees={pool}
          recordedEmployeeIds={recordedIds}
          onSaved={onChanged}
        />
      </div>
      {recorded.length === 0 ? (
        <p className="text-xs text-muted-foreground">No commissions recorded for this closing yet.</p>
      ) : (
        <div className="space-y-1">
          {recorded.map((r) => (
            <div key={r.id} className="flex justify-between text-sm" data-testid={`closing-commission-${closing.id}-${r.employee_id}`}>
              <span>{r.employee_name}</span>
              <span className="font-semibold text-emerald-700">{formatCurrency(r.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between text-xs pt-1 border-t text-muted-foreground">
            <span>Total commission</span>
            <span className="font-semibold">{formatCurrency(totalCommission)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function RecordClosingCommissionDialog({ closing, projectId, managerProfit, employees, recordedEmployeeIds, onSaved }) {
  const [open, setOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [base, setBase] = useState(String(managerProfit || ''));
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setBase(String(managerProfit || '')); setEmployeeId(''); setPreview(null); }
  }, [open, managerProfit]);

  const available = employees.filter((e) => !recordedEmployeeIds.has(e.id));

  useEffect(() => {
    const amt = parseFloat(base);
    if (!open || !employeeId || !amt) { setPreview(null); return; }
    let active = true;
    employeesApi.previewCommission(employeeId, amt)
      .then((r) => { if (active) setPreview(r.data); })
      .catch(() => { if (active) setPreview(null); });
    return () => { active = false; };
  }, [open, employeeId, base]);

  const save = async () => {
    const amt = parseFloat(base);
    if (!employeeId || !amt) { toast.error('Pick an employee and a profit base'); return; }
    setSaving(true);
    try {
      const res = await employeesApi.recordCommission({
        employee_id: employeeId,
        project_id: projectId,
        closing_id: closing.id,
        net_profit_to_land_finder: amt,
        description: `Commission — ${closing.closing_date} lot-sale closing`,
      });
      toast.success(`Commission of ${formatCurrency(res.data.commission_amount)} recorded`);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record commission');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" data-testid={`record-commission-btn-${closing.id}`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Record Commission
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record Commission</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-muted-foreground -mt-2">
          Record an employee commission on this lot-sale closing. Commission is tiered on the Land Finder net profit ({formatCurrency(managerProfit)}).
        </DialogDescription>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="rounded-sm" data-testid="closing-commission-employee-select">
                <SelectValue placeholder={available.length ? 'Select employee…' : 'All employees already recorded'} />
              </SelectTrigger>
              <SelectContent>
                {available.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}{e.is_eligible ? '' : ' (not flagged eligible)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Net Profit to Land Finder ($)</Label>
            <Input type="number" value={base} onChange={(e) => { setBase(e.target.value); setPreview(null); }}
              className="rounded-sm" data-testid="closing-commission-base-input" />
            <p className="text-[11px] text-muted-foreground">Defaults to this closing&apos;s manager (LF) profit. Adjust if needed.</p>
          </div>
          {preview && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="closing-commission-preview">
              <p className="text-xs uppercase tracking-wide text-emerald-700 font-medium">Commission Preview</p>
              <p className="text-2xl font-heading font-bold text-emerald-600 mt-1">{formatCurrency(preview.commission_amount)}</p>
              <div className="mt-1 text-xs text-emerald-700 space-y-0.5">
                {(preview.tier_breakdown || []).map((t, i) => (
                  <p key={i}>Tier {t.tier}: {(t.rate * 100).toFixed(0)}% on {formatCurrency(t.amount_applied)} = {formatCurrency(t.commission)}</p>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save}
            disabled={saving || !employeeId || !parseFloat(base)} data-testid="save-closing-commission-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Record Commission
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordClosingDialog({ projectId, heldLots, allLots, onSaved, existingClosing, trigger }) {
  const isEdit = !!existingClosing;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ closing_date: '', gross_resale_price: '', actual_closing_costs: '' });
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // Hydrate from the existing closing when editing.
  useEffect(() => {
    if (open && isEdit) {
      setForm({
        closing_date: existingClosing.closing_date || '',
        gross_resale_price: String(existingClosing.gross_resale_price ?? ''),
        actual_closing_costs: String(existingClosing.actual_closing_costs ?? ''),
      });
      setSelected(existingClosing.lot_ids || []);
    }
  }, [open, isEdit, existingClosing]);

  const lotOptions = isEdit ? (allLots || []) : (heldLots || []);

  const toggleLot = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const payload = useCallback(() => ({
    closing_date: form.closing_date,
    lot_ids: selected,
    gross_resale_price: parseFloat(form.gross_resale_price) || 0,
    actual_closing_costs: parseFloat(form.actual_closing_costs) || 0,
  }), [form, selected]);

  useEffect(() => {
    if (!open || !form.closing_date || selected.length === 0 || !form.gross_resale_price) { setPreview(null); return; }
    let active = true;
    subdivideApi.previewClosing(projectId, payload())
      .then((r) => { if (active) setPreview(r.data); })
      .catch(() => { if (active) setPreview(null); });
    return () => { active = false; };
  }, [open, form, selected, projectId, payload]);

  const save = async () => {
    if (!form.closing_date || selected.length === 0 || !form.gross_resale_price) {
      toast.error('Pick a date, at least one lot, and a resale price'); return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await subdivideApi.updateClosing(existingClosing.id, payload());
        toast.success('Closing updated');
      } else {
        await subdivideApi.createClosing(projectId, payload());
        toast.success('Closing recorded');
      }
      setOpen(false);
      if (!isEdit) { setForm({ closing_date: '', gross_resale_price: '', actual_closing_costs: '' }); setSelected([]); }
      setPreview(null);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save closing'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" className="rounded-sm" data-testid="record-closing-btn">
            <Plus className="w-4 h-4 mr-1" /> Record a Closing
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Closing' : 'Record a Closing'}</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-muted-foreground -mt-2">
          Select the lots sold in this closing, enter the resale price and actual closing costs, and review the live waterfall before saving.
        </DialogDescription>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Lots sold in this closing</Label>
            <div className="mt-2 space-y-2 border rounded-sm p-3 max-h-40 overflow-y-auto">
              {lotOptions.length === 0 && <p className="text-sm text-muted-foreground">No lots available.</p>}
              {lotOptions.map((l) => (
                <label key={l.lot_id} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`select-lot-${l.lot_id}`}>
                  <Checkbox checked={selected.includes(l.lot_id)} onCheckedChange={() => toggleLot(l.lot_id)} />
                  <span className="flex-1">{l.name} — {fmtAcres(l.acres)} ac</span>
                  <span className="text-muted-foreground">basis {formatCurrency(l.cost_basis)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Closing Date</Label>
              <Input type="date" value={form.closing_date} onChange={(e) => setForm({ ...form, closing_date: e.target.value })} data-testid="closing-date-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Gross Resale Price</Label>
              <Input type="number" value={form.gross_resale_price} onChange={(e) => setForm({ ...form, gross_resale_price: e.target.value })} placeholder="135000" data-testid="closing-resale-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Actual Closing Costs</Label>
              <Input type="number" value={form.actual_closing_costs} onChange={(e) => setForm({ ...form, actual_closing_costs: e.target.value })} placeholder="8833.19" data-testid="closing-costs-input" />
            </div>
          </div>

          {preview && (
            <div className="bg-muted/40 rounded-sm p-4 text-sm" data-testid="closing-preview">
              <p className="text-xs font-medium text-muted-foreground mb-2">Live Waterfall Preview · Investor split {preview.investor_split_pct}% (hold {preview.days_held}d)</p>
              <PayoutRow label="Net Cash Available" value={preview.net_cash_available} strong />
              <PayoutRow label="Tier 1 — Capital Rebalance" value={preview.tier1_rebalance_total} />
              <PayoutRow label="Tier 2 — Sold-Lot Capital" value={preview.tier2_sold_lot_capital} />
              <PayoutRow label="Tier 3 — Net Profit" value={preview.cash_after_tier2_net_profit} strong />
              <div className="border-t mt-2 pt-2">
                {(preview.partner_wires || []).map((w) => (
                  <div key={w.partner_id} className="flex justify-between py-1">
                    <span>{w.partner_name} wire</span><span className="font-semibold">{formatCurrency(w.total_wire)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-1">
                  <span>Manager wire</span><span className="font-semibold">{formatCurrency(preview.manager_wire?.total_wire)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="rounded-sm" onClick={save} disabled={saving} data-testid="save-closing-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {isEdit ? 'Save Changes' : 'Record Closing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
